import { ReactNode } from 'react';

import { useTaskItem } from 'components/task-item/useTaskItem';
import { FOCO_VISIVEL } from 'utils/foco';

import { EtapaDaAgenda, periodoDa } from './AgendaDaRealizacao';
import DetalhesDaRealizacao from './DetalhesDaRealizacao';
import { NomeDoResponsavel } from './TrilhaDaRealizacao';

type ModeloDoCartao = ReturnType<typeof useTaskItem>;

export interface BotaoMaisInformacoesProps {
  aberto: boolean;
  /** Id do painel que o botão abre (`aria-controls`). */
  controla: string;
  onClick: () => void;
}

/** "Mais informações" / "Menos informações" do rodapé do cartão (RDV-21). */
export function BotaoMaisInformacoes({ aberto, controla, onClick }: BotaoMaisInformacoesProps) {
  return (
    <button
      type="button"
      aria-expanded={aberto}
      aria-controls={controla}
      onClick={onClick}
      className={`ml-auto inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.04em] text-brand-blue underline underline-offset-[3px] ${FOCO_VISIVEL}`}
    >
      {aberto ? 'Menos informações' : 'Mais informações'}
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        className={aberto ? 'rotate-180' : ''}
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>
  );
}

/** Ponto da etapa, decorativo (o status está no texto): verde concluída, rosa atual, cinza ainda não alcançada. */
function corDoPonto(etapa: EtapaDaAgenda): string {
  if (etapa.metades.includes('devolvida')) return 'bg-urgent';
  if (etapa.metades.includes('concluida')) return 'bg-brand-green';
  if (etapa.atual) return 'bg-brand-pink';
  if (etapa.metades.includes('prazo')) return 'bg-brand-yellow';
  return 'bg-surface-300';
}

/** Texto da etapa: o status do `TaskItem`; sem status, "Próxima etapa" nas etapas depois da atual, como no design. */
function statusNaAgenda(etapa: EtapaDaAgenda, indice: number, indiceDaAtual: number): string {
  if (etapa.status) return etapa.status;
  return indiceDaAtual >= 0 && indice > indiceDaAtual ? 'Próxima etapa' : '';
}

/**
 * Agenda de apreciação do cartão do participante (RDV-21), como em `participante-mais-informacoes-1440.png`: em cada
 * etapa o ponto da cor da etapa, o nome em negrito, "De dd/mm a dd/mm" no calendário da fase, o status e os
 * responsáveis. A `AgendaDaRealizacao` das Ações ECOA fica como está.
 * SPEC_DEVIATION: nomes e datas em `content`; o `text-soft` do design dá 4,43:1 no cinza do painel.
 */
function AgendaDoCartao({ etapas }: { etapas: EtapaDaAgenda[] }) {
  const indiceDaAtual = etapas.findIndex(etapa => etapa.atual);

  return (
    <ol aria-label="Etapas da apreciação" className="m-0 grid list-none grid-cols-2 gap-3 p-0 md:grid-cols-4">
      {etapas.map((etapa, indice) => {
        const status = statusNaAgenda(etapa, indice, indiceDaAtual);
        return (
          <li key={etapa.titulo} aria-current={etapa.atual ? 'step' : undefined} className="flex min-w-0 flex-col gap-[3px]">
            <p className="m-0 flex items-center gap-1.5 font-display text-xs font-bold text-content">
              <span aria-hidden="true" data-ponto className={`h-2 w-2 shrink-0 rounded-full ${corDoPonto(etapa)}`} />
              {etapa.titulo}
            </p>
            {etapa.periodo && <p className="m-0 text-[10.5px] text-content">{`De ${etapa.periodo}`}</p>}
            {status && <p className="m-0 text-[11px] leading-[1.4] text-content">{status}</p>}
            {etapa.responsaveis.length > 0 && (
              <div className="flex flex-col gap-0.5 text-[11px] leading-[1.4]">
                {etapa.responsaveis.map(pessoa => (
                  <span key={pessoa.chave}>
                    <NomeDoResponsavel pessoa={pessoa} fundo="cinza" />
                  </span>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export interface PainelMaisInformacoesProps {
  id: string;
  modelo: ModeloDoCartao;
  etapas: EtapaDaAgenda[];
  acoesDoParticipante: (pessoa: any) => ReactNode;
}

/**
 * Conteúdo de "Mais informações" (RDV-21): a agenda com o período de cada etapa no calendário da fase da realização,
 * o status e os responsáveis, e os detalhes, resultados, feedback e participantes do "Ver mais" de hoje. Sem a
 * leitura completa (`description`, `team`, `target`), mostra o aviso de carregamento como hoje.
 */
export function PainelMaisInformacoes({ id, modelo, etapas, acoesDoParticipante }: PainelMaisInformacoesProps) {
  const { data } = modelo;
  const carregado = !!data.description && !!data.team && !!data.target;
  const agenda = etapas.map(etapa => ({ ...etapa, periodo: periodoDa(etapa.titulo, modelo.stepTask) }));

  return (
    <div id={id}>
      {modelo.oppened &&
        (carregado ? (
          <DetalhesDaRealizacao
            data={data}
            getUserTeam={modelo.getUserTeam}
            getFormattedDate={modelo.getFormattedDate}
            getStepLabel={modelo.getStepLabel}
            ocultarPercentual={modelo.ecoa}
            acoesDoParticipante={acoesDoParticipante}
            noCartaoDoParticipante
            inicio={
              <section aria-labelledby={`${id}-agenda`}>
                <h4 id={`${id}-agenda`} className="m-0 mb-2.5 text-[10px] font-bold uppercase tracking-[0.06em] text-content">
                  Agenda de apreciação
                </h4>
                <AgendaDoCartao etapas={agenda} />
              </section>
            }
          />
        ) : (
          <p role="status" className="m-0 mt-4 text-center text-xs text-content-soft">
            Carregando informações…
          </p>
        ))}
    </div>
  );
}

/** Abre ou fecha "Mais informações"; sem a leitura completa, pede a releitura da realização como o `TaskItem`. */
export function alternarMaisInformacoes(modelo: ModeloDoCartao) {
  const vaiAbrir = !modelo.oppened;
  if (vaiAbrir && (!modelo.data.team || !modelo.data.target)) modelo.handleReload();
  modelo.setOppened(vaiAbrir);
}
